import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import Chart from "react-apexcharts";
import { FaArrowLeft, FaChevronRight, FaServer, FaCheckCircle, FaExclamationTriangle, FaClock, FaCube, FaProjectDiagram } from "react-icons/fa";
import { fetchProjectWorkflows, fetchTenantProjects } from "../services/analyticsService";
import { getNodeTitle } from "../utils/nodeMappings";

/**
 * @file UserDetailModal.js
 * @description Drill-down analytics modal supporting navigation through Tenant -> Project -> Workflow -> Node levels.
 */
export default function UserDetailModal({ show, data, timeFilter, customRange, onClose }) {
  // Navigation Stack: Array of view objects { type: 'tenant'|'project'|'workflow', id: 1, name: '...' }
  const [viewStack, setViewStack] = useState([]);
  const [currentView, setCurrentView] = useState(null);
  const [activeTabLoading, setLoadingWorkflows] = useState(false);

  const [tenantProjects, setTenantProjects] = useState([]);
  const [tenantNodeMetrics, setTenantNodeMetrics] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);

  // Sorting State
  const [tenantSortConfig, setTenantSortConfig] = useState({ key: null, direction: 'asc' });
  const [workflowSortConfig, setWorkflowSortConfig] = useState({ key: null, direction: 'asc' });

  // Reset stack on open
  useEffect(() => {
    if (show && data) {
      const initialView = { type: 'tenant', name: data.name || data.user_info?.display_name || "Tenant", data: data };
      setViewStack([initialView]);
      setCurrentView(initialView);

      setTenantNodeMetrics([]);

      // Fetch Projects for this tenant
      const tenantId = data.org_id || data.tenant_id || data.admin_info?.org_id;
      if (tenantId) {
        setLoadingProjects(true);
        fetchTenantProjects(tenantId, timeFilter, customRange).then(res => {
          if (res) {
            setTenantProjects(res.projects || []);
            setTenantNodeMetrics(res.tenant_node_metrics || []);
          } else {
            setTenantProjects([]);
            setTenantNodeMetrics([]);
          }
          setLoadingProjects(false);
        });
      } else {
        setTenantProjects([]);
      }
    }
  }, [show, data, timeFilter, customRange]);

  if (!show || !currentView) return null;



  // --- NAVIGATION HANDLERS ---
  const navigateTo = (view) => {
    const newStack = [...viewStack, view];
    setViewStack(newStack);
    setCurrentView(view);
  };

  const navigateBack = () => {
    if (viewStack.length > 1) {
      const newStack = [...viewStack];
      newStack.pop();
      setViewStack(newStack);
      setCurrentView(newStack[newStack.length - 1]);
    } else {
      onClose();
    }
  };

  const navigateToBreadcrumb = (index) => {
    const newStack = viewStack.slice(0, index + 1);
    setViewStack(newStack);
    setCurrentView(newStack[newStack.length - 1]);
  };

  // --- RENDERERS ---
  const renderKPIs = (kpis) => (
    <div className="row g-3 mb-4">
      {[
        { label: "Volume", val: kpis.executions?.toLocaleString() || kpis.executions_used || "0", icon: FaServer, color: "text-primary" },
        { label: "Success Rate", val: `${(kpis.successRate || 98.5).toFixed(1)}%`, icon: FaCheckCircle, color: "text-success" },
        { label: "Failures", val: kpis.failures || kpis.failed || 12, icon: FaExclamationTriangle, color: "text-danger" },
        { label: "Avg Latency", val: `${kpis.avgLatency || 240}ms`, icon: FaClock, color: "text-warning" }
      ].map((k, i) => (
        <div key={i} className="col-md-3">
          <div className="card border-0 shadow-sm p-3 h-100">
            <div className="d-flex align-items-center gap-3">
              <div className={`p-2 rounded bg-light ${k.color}`}><k.icon size={18} /></div>
              <div>
                <div className="text-muted small fw-bold text-uppercase" style={{ fontSize: '0.7rem' }}>{k.label}</div>
                <h5 className="fw-bold mb-0">{k.val}</h5>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderBreadcrumbs = () => (
    <nav aria-label="breadcrumb">
      <ol className="breadcrumb mb-0">
        {viewStack.map((view, i) => (
          <li key={i} className={`breadcrumb-item ${i === viewStack.length - 1 ? 'active fw-bold' : ''}`}>
            {i === viewStack.length - 1 ? (
              view.name
            ) : (
              <a href="#" onClick={(e) => { e.preventDefault(); navigateToBreadcrumb(i); }} className="text-decoration-none">
                {view.name}
              </a>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );

  // --- SORTING HELPERS ---
  const renderSortArrow = (config, key) => {
    if (config.key !== key) return <span className="text-muted ms-1" style={{ fontSize: '0.7em' }}>↕</span>;
    return config.direction === 'asc' ? <span className="ms-1">↑</span> : <span className="ms-1">↓</span>;
  };

  const sortData = (data, config) => {
    if (!config.key) return data;
    return [...data].sort((a, b) => {
      let aVal = a[config.key];
      let bVal = b[config.key];

      // Handle nested numeric values if needed or just straight values
      // For our simplified data structures:
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toString().toLowerCase();
      }

      if (aVal < bVal) return config.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return config.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };


  // --- VIEW CONTENTS ---

  // 1. TENANT VIEW
  const renderTenantView = () => {
    // Helper to handle project click
    const handleProjectClick = async (project, index) => {
      // If we have real IDs, fetch data
      // We need tenantId (org_id) and projectId
      // Check for org_id in multiple likely places
      const tenantId = data.org_id || data.tenant_id || data.admin_info?.org_id;
      const projectId = project.project_id;

      let projectData = null;

      if (tenantId && projectId) {
        setLoadingWorkflows(true);
        const apiRes = await fetchProjectWorkflows(tenantId, projectId, timeFilter, customRange);
        setLoadingWorkflows(false);
        if (apiRes) {
          projectData = {
            name: project.project_name,
            isRealData: true,
            raw: apiRes,
            // Map API response to Component structure
            // Calculate Project-level KPIs from the workflow list
            kpis: {
              executions: apiRes.workflows.reduce((acc, wf) => acc + (wf.metrics?.total_executions || 0), 0),
              failures: apiRes.workflows.reduce((acc, wf) => acc + (wf.metrics?.failed_executions || 0), 0),
              successRate: 0,
              avgLatency: 0
            },
            workflows: apiRes.workflows.map(wf => ({
              id: wf.workflow_id,
              name: wf.workflow_name,
              executions: wf.metrics.total_executions,
              successRate: wf.metrics.success_rate,
              avgLatency: wf.metrics.avg_latency_ms,
              failures: wf.metrics.failed_executions,
              agents: 0
            }))
          };

          // Calculate proper aggregated rates
          const totalExecs = projectData.kpis.executions;
          if (totalExecs > 0) {
            const weightedSuccess = apiRes.workflows.reduce((acc, wf) => acc + (wf.metrics.success_rate * wf.metrics.total_executions), 0);
            projectData.kpis.successRate = weightedSuccess / totalExecs;

            const weightedLatency = apiRes.workflows.reduce((acc, wf) => acc + (wf.metrics.avg_latency_ms * wf.metrics.total_executions), 0);
            projectData.kpis.avgLatency = weightedLatency / totalExecs;
          }
        }
      }

      if (projectData) {
        navigateTo({ type: 'project', name: project.project_name, data: projectData });
      } else {
        console.warn("No real data could be fetched for this project.");
        // Don't navigate to mock data
      }
    };

    const handleTenantSort = (key) => {
      let direction = 'asc';
      if (tenantSortConfig.key === key && tenantSortConfig.direction === 'asc') direction = 'desc';
      setTenantSortConfig({ key, direction });
    };

    const sortedTenantMetrics = sortData(tenantNodeMetrics || [], tenantSortConfig);

    return (
      <>
        {/* PROJECTS LIST */}
        <div className="card border-0 shadow-sm rounded-3 mb-4">
          <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
            <h6 className="fw-bold mb-0">Projects</h6>
            {loadingProjects && <span className="spinner-border spinner-border-sm text-primary"></span>}
          </div>
          <div className="list-group list-group-flush">
            {tenantProjects.length > 0 ? tenantProjects.map((p, i) => (
              <div key={i}
                className="list-group-item list-group-item-action d-flex justify-content-between align-items-center py-3"
                style={{ cursor: 'pointer' }}
                onClick={() => handleProjectClick(p, i)}
              >
                <div className="d-flex align-items-center gap-3">
                  <div className="bg-light p-2 rounded text-secondary"><FaProjectDiagram /></div>
                  <div>
                    <span className="fw-medium d-block">{p.project_name}</span>
                    {p.project_id && <span className="small text-muted font-monospace">{p.project_id.slice(0, 8)}...</span>}
                  </div>
                </div>
                <div className="d-flex align-items-center gap-3">
                  {activeTabLoading && <span className="spinner-border spinner-border-sm text-muted"></span>}
                  <FaChevronRight className="text-muted small" />
                </div>
              </div>
            )) : (
              <div className="p-4 text-center text-muted">
                {loadingProjects ? "Loading Projects..." : "No projects found for this tenant."}
              </div>
            )}
          </div>
        </div>

        {/* TENANT NODE METRICS TABLE (API Data) */}
        {tenantNodeMetrics && tenantNodeMetrics.length > 0 && (
          <div className="card border-0 shadow-sm rounded-3">
            <div className="card-header bg-white py-3">
              <h6 className="fw-bold mb-0">Tenant Node Metrics</h6>
            </div>
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0 text-sm">
                <thead className="bg-light">
                  <tr style={{ cursor: 'pointer' }}>
                    <th className="ps-4" onClick={() => handleTenantSort('node_type')}>Node Type {renderSortArrow(tenantSortConfig, 'node_type')}</th>
                    <th onClick={() => handleTenantSort('executions')}>Executions {renderSortArrow(tenantSortConfig, 'executions')}</th>
                    <th onClick={() => handleTenantSort('success_rate')}>Success % {renderSortArrow(tenantSortConfig, 'success_rate')}</th>
                    <th onClick={() => handleTenantSort('avg_latency')}>Avg Latency {renderSortArrow(tenantSortConfig, 'avg_latency')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedTenantMetrics.map((n, i) => (
                    <tr key={i}>
                      <td className="ps-4 fw-medium text-primary">{getNodeTitle(n.node_type)}</td>
                      <td>{n.executions}</td>
                      <td>
                        <span className={`badge ${n.success_rate >= 95 ? 'bg-success-subtle text-success' : 'bg-warning-subtle text-warning'}`}>
                          {n.success_rate}%
                        </span>
                      </td>
                      <td>{(n.avg_latency / 1000).toFixed(2)}s</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </>
    );
  };

  // 2. PROJECT VIEW
  const renderProjectView = (projectData) => {
    const handleWorkflowSort = (key) => {
      let direction = 'asc';
      if (workflowSortConfig.key === key && workflowSortConfig.direction === 'asc') direction = 'desc';
      setWorkflowSortConfig({ key, direction });
    };

    const sortedWorkflows = sortData(projectData.workflows || [], workflowSortConfig);

    return (
      <>


        {/* WORKFLOWS TABLE */}
        <div className="card border-0 shadow-sm rounded-3 mb-4">
          <div className="card-header bg-white py-3"><h6 className="fw-bold mb-0">Workflows</h6></div>
          <table className="table table-hover align-middle mb-0">
            <thead className="bg-light">
              <tr style={{ cursor: 'pointer' }}>
                <th className="ps-4" onClick={() => handleWorkflowSort('name')}>Workflow Name {renderSortArrow(workflowSortConfig, 'name')}</th>
                <th onClick={() => handleWorkflowSort('executions')}>Executions {renderSortArrow(workflowSortConfig, 'executions')}</th>
                <th onClick={() => handleWorkflowSort('successRate')}>Success % {renderSortArrow(workflowSortConfig, 'successRate')}</th>
                <th onClick={() => handleWorkflowSort('avgLatency')}>Avg Latency {renderSortArrow(workflowSortConfig, 'avgLatency')}</th>
                <th className="pe-4 text-end" onClick={() => handleWorkflowSort('failures')}>Failures {renderSortArrow(workflowSortConfig, 'failures')}</th>
              </tr>
            </thead>
            <tbody>
              {sortedWorkflows.length > 0 ? sortedWorkflows.map((wf, i) => (
                <tr key={i}>
                  <td className="ps-4 fw-medium text-dark"><FaCube className="me-2 mb-1 small text-secondary" /> {wf.name}</td>
                  <td>{wf.executions}</td>
                  <td>
                    <span className={`badge ${wf.successRate > 95 ? 'bg-success-subtle text-success' : 'bg-warning-subtle text-warning'}`}>
                      {wf.successRate.toFixed(1)}%
                    </span>
                  </td>
                  <td>{wf.avgLatency ? (wf.avgLatency / 1000).toFixed(2) : 0}s</td>
                  <td className="pe-4 text-end">{wf.failures}</td>
                </tr>
              )) : (
                <tr><td colSpan="5" className="text-center py-4 text-muted">No workflows found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </>
    );
  };


  // 3. WORKFLOW VIEW (NODES)
  const renderWorkflowView = (wfData) => {
    return (
      <>
        {renderKPIs(wfData.kpis)}
        <div className="card border-0 shadow-sm rounded-3">
          <div className="card-header bg-white py-3"><h6 className="fw-bold mb-0">Node Performance</h6></div>
          <table className="table table-hover align-middle mb-0">
            <thead className="bg-light">
              <tr>
                <th className="ps-4">Node Type</th>
                <th>Executions</th>
                <th>Success %</th>
                <th>Latency</th>
                <th>Retries</th>
                <th>Timeout</th>
                <th className="pe-4 text-end">Errors</th>
              </tr>
            </thead>
            <tbody>
              {wfData.nodes.map((node, i) => (
                <tr key={i}>
                  <td className="ps-4 fw-medium">{getNodeTitle(node.type)}</td>
                  <td>{node.executions}</td>
                  <td>
                    <span className={`badge ${node.success > 98 ? 'bg-success-subtle text-success' : 'bg-warning-subtle text-warning'}`}>
                      {node.success}%
                    </span>
                  </td>
                  <td>{node.latency}ms</td>
                  <td>{node.retries}</td>
                  <td>{node.timeout}</td>
                  <td className="pe-4 text-end text-danger fw-bold">{node.errors}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  return (
    <Modal show={show} onHide={onClose} size="xl" centered backdrop="static" scrollable>
      <Modal.Header className="border-0 pb-0 d-block">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center gap-3">
            {viewStack.length > 1 && (
              <button className="btn btn-sm btn-light rounded-circle" onClick={navigateBack}><FaArrowLeft /></button>
            )}
            <h5 className="modal-title fw-bold">
              Drill Down Analytics
            </h5>
          </div>
          <button className="btn-close" onClick={onClose}></button>
        </div>
        <div className="bg-light p-2 rounded mb-3">
          {renderBreadcrumbs()}
        </div>
      </Modal.Header>
      <Modal.Body className="bg-light p-4">
        {currentView.type === 'tenant' && renderTenantView()}
        {currentView.type === 'project' && renderProjectView(currentView.data)}
        {currentView.type === 'workflow' && renderWorkflowView(currentView.data)}
      </Modal.Body>
    </Modal>
  );
}
